import React, { useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { teamLabel } from "@/components/manage/matchUtils";
import { fmtDateTime } from "@/components/common";
import { buildScheduleSlots } from "@/components/manage/scheduleSlots";
import { buildMatchBreaks } from "@/components/manage/scheduleTimeline";
import ScheduleTimeRange, { ScheduleBreak } from "@/components/manage/ScheduleTimeRange";
import { GripVertical, Lock, CalendarClock } from "lucide-react";

function nearestSlot(iso, slots) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  let best = slots[0], diff = Infinity;
  for (const s of slots) { const d = Math.abs(s.ms - t); if (d < diff) { diff = d; best = s; } }
  return best;
}

export default function ScheduleGrid({ tournament: t, matches, referees, teamMap, warnIds, onMove, onSetReferee, onEditTime }) {
  const flds = t.fields || [];
  const slots = useMemo(() => buildScheduleSlots(matches, t), [matches, t]);
  const breaks = useMemo(() => buildMatchBreaks(matches, t), [matches, t]);
  const fieldWidth = 210;
  const gridStyle = { gridTemplateColumns: `120px repeat(${flds.length}, minmax(${fieldWidth}px, 1fr))` };
  const gridMinWidth = 120 + flds.length * fieldWidth;

  // cellMap[fieldId][slotIndex] = [matches]
  const cellMap = useMemo(() => {
    const map = {};
    flds.forEach((f) => (map[f.id] = {}));
    matches.forEach((m) => {
      const fid = m.field_id || (flds[0] && flds[0].id);
      const slot = nearestSlot(m.scheduled_time, slots) || slots[0];
      if (!map[fid]) map[fid] = {};
      (map[fid][slot.index] = map[fid][slot.index] || []).push(m);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, slots, flds]);

  const onDragEnd = (res) => {
    if (!res.destination) return;
    const [fieldId, slotIdxStr] = res.destination.droppableId.split("::");
    const slot = slots[parseInt(slotIdxStr)];
    onMove(res.draggableId, { field_id: fieldId, scheduled_time: slot.iso });
  };

  return (
    <div className="overflow-x-auto rounded-xl border" data-testid="schedule-grid">
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ minWidth: gridMinWidth }}>
          {/* header */}
          <div className="sticky top-0 z-10 grid border-b bg-muted/40" style={gridStyle}>
            <div className="p-2 text-label text-muted-foreground">Time</div>
            {flds.map((f) => (
              <div key={f.id} className="min-w-0 border-l p-2 text-center text-sm font-semibold" data-testid={`grid-col-${f.id}`}>{f.name}</div>
            ))}
          </div>
          {/* rows */}
          {slots.map((slot) => (
            <div key={slot.index} className="grid border-b last:border-b-0" style={gridStyle}>
              <div className="p-2 text-xs font-medium text-muted-foreground">{fmtDateTime(slot.iso, t.timezone)}</div>
              {flds.map((f) => {
                const cellId = `${f.id}::${slot.index}`;
                const cellMatches = (cellMap[f.id] && cellMap[f.id][slot.index]) || [];
                return (
                  <Droppable droppableId={cellId} key={cellId}>
                    {(prov, snap) => (
                      <div ref={prov.innerRef} {...prov.droppableProps}
                        className={`min-h-[64px] min-w-0 space-y-1.5 border-l p-1.5 transition-colors ${snap.isDraggingOver ? "bg-primary/5 outline-dashed outline-1 outline-primary/40" : ""}`}
                        data-testid={`grid-cell-${cellId}`}>
                         {cellMatches.map((m, i) => (
                          <React.Fragment key={m.id}>
                          <Draggable key={m.id} draggableId={m.id} index={i} isDragDisabled={m.status === "final"}>
                            {(p, s) => (
                              <div ref={p.innerRef} {...p.draggableProps}
                                className={`rounded-lg border bg-card p-2 text-xs ${s.isDragging ? "dnd-dragging" : ""} ${warnIds.has(m.id) ? "border-accent/60" : ""}`}
                                data-testid={`grid-match-${m.id}`}>
                                <div className="flex items-center justify-between">
                                  <span className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">{m.round_name}</span>
                                  {m.status === "final" ? <Lock className="h-3 w-3 text-emerald-600" />
                                    : m.status === "live" ? <span className="live-dot" />
                                    : <span {...p.dragHandleProps} className="cursor-grab text-muted-foreground"><GripVertical className="h-3.5 w-3.5" /></span>}
                                </div>
                                <div className="mt-1 font-medium leading-tight">
                                  <div className="truncate">{teamLabel(m, "a", teamMap)}</div>
                                  <div className="truncate text-muted-foreground">{teamLabel(m, "b", teamMap)}</div>
                                </div>
                                <button type="button" disabled={m.status === "final"} onClick={() => onEditTime(m)}
                                  className="mt-1.5 flex w-full items-start gap-1 rounded border px-1.5 py-1 text-left text-[11px] hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={`Change start time for ${teamLabel(m, "a", teamMap)} vs ${teamLabel(m, "b", teamMap)}`}
                                  data-testid={`grid-edit-time-${m.id}`}>
                                  <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
                                  <ScheduleTimeRange match={m} tournament={t} />
                                </button>
                                <Select value={m.referee_id || "none"} disabled={m.status === "final"}
                                  onValueChange={(v) => onSetReferee(m.id, v === "none" ? null : v)}>
                                  <SelectTrigger className="mt-1.5 h-7 text-[11px]" data-testid={`grid-ref-${m.id}`}><SelectValue placeholder="Referee" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No referee</SelectItem>
                                    {referees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                           </Draggable>
                           {breaks.has(m.id) && <ScheduleBreak pause={breaks.get(m.id)} tournament={t} />}
                          </React.Fragment>
                        ))}
                        {prov.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
